import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Microsoft 365 / Outlook (delegert OAuth per brukar).
 * Backend: /api/integrations/m365/*  og  /api/reports/{id}/(email-draft|emails|send-email)
 */

export const PROFILE_PATH = "/profil";
export const M365_CHANGED = "vms:m365-changed";
export const notifyM365Changed = () => window.dispatchEvent(new Event(M365_CHANGED));

export const fetchM365Status = () => api.get("/integrations/m365/status").then((r) => r.data);
export const fetchM365Setup = () => api.get("/integrations/m365/setup").then((r) => r.data);

/** Startar OAuth hos Microsoft. Nettlesaren blir sendt til Microsoft og kjem tilbake til `returnTo` (frontend-sti). */
export async function connectOutlook(returnTo) {
  const rt = returnTo || `${window.location.pathname}${window.location.search}` || PROFILE_PATH;
  const r = await api.post("/integrations/m365/connect", { return_to: rt });
  if (!r.data?.url) throw new Error("Mangla autorisasjons-URL");
  window.location.assign(r.data.url);
}

export const disconnectOutlook = () =>
  api.delete("/integrations/m365/connection").then((r) => {
    notifyM365Changed();
    return r.data;
  });

export const sendTestMail = () => api.post("/integrations/m365/test").then((r) => r.data);

/** Status for innlogga brukar: {configured, connected, connection:{account_email,…}}. */
export function useM365Status(enabled = true) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);

  const reload = useCallback(() => {
    if (!enabled) return Promise.resolve(null);
    setLoading(true);
    return fetchM365Status()
      .then((s) => {
        setStatus(s);
        setError(null);
        return s;
      })
      .catch((e) => {
        setError(e?.response?.data?.detail || "Kunne ikkje hente Outlook-status.");
        return null;
      })
      .finally(() => setLoading(false));
  }, [enabled]);

  useEffect(() => {
    reload();
    window.addEventListener(M365_CHANGED, reload);
    return () => window.removeEventListener(M365_CHANGED, reload);
  }, [reload]);

  return {
    status,
    loading,
    error,
    reload,
    configured: !!status?.configured,
    connected: !!status?.connected,
    connection: status?.connection || null,
  };
}

// ---- Callback frå Microsoft (?m365=connected|error&m365_msg=…) -------------------------------
export const readM365Callback = (params) => {
  const s = params.get("m365");
  if (!s) return null;
  return { status: s, msg: params.get("m365_msg") || "" };
};

export const stripM365Params = (params) => {
  const p = new URLSearchParams(params);
  p.delete("m365");
  p.delete("m365_msg");
  return p;
};

export const toastM365Callback = (cb) => {
  if (!cb) return;
  if (cb.status === "connected") {
    toast.success(cb.msg ? `Outlook er kopla til (${cb.msg}).` : "Outlook er kopla til.", { description: "Rapportar du sender går no frå di eiga e-postadresse." });
  } else {
    toast.error(cb.msg || "Kunne ikkje kople til Outlook.", { description: "Prøv igjen, eller kontakt administrator om feilen held fram." });
  }
  notifyM365Changed();
};

// ---- E-post for rapportar -----------------------------------------------------------------------
export const fetchEmailDraft = (reportId) => api.get(`/reports/${reportId}/email-draft`).then((r) => r.data);
export const fetchReportEmails = (reportId) => api.get(`/reports/${reportId}/emails`).then((r) => r.data?.items || []);
export const sendReportEmail = (reportId, payload) => api.post(`/reports/${reportId}/send-email`, payload).then((r) => r.data);

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
/** «a@b.no, c@d.no; e@f.no» → ["a@b.no", "c@d.no", "e@f.no"] */
export const parseEmails = (text) =>
  String(text || "")
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
